/**
 * Pure viewport-clamping math for the module options-menu popover position,
 * extracted verbatim from the `handleOpenOptions` listener in
 * EventListModule.tsx (develop ~L184-210).
 *
 * Three placement modes, in priority order:
 *   1. `anchorRect` provided (the gear button's bounding rect) → anchor the
 *      panel below/right of it.
 *   2. else `moduleRect` provided (the module root's bounding rect) → anchor
 *      to the module's top-right.
 *   3. else neither → the default `{ top: 56, left: 12 }`.
 *
 * `panelWidth` is clamped to [0, viewportWidth-24] capped at 190. All
 * coordinates are clamped so the panel never escapes a 12px viewport gutter
 * (top clamps to viewportHeight-120 to leave room for the panel body).
 *
 * Behavior is pinned by `computeOptionsMenuPosition.char.test.ts`. Keeping this
 * pure (no DOM reads) means the hook only has to pass in the measured rects.
 */
export interface MenuRect {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface MenuPosition {
  top: number;
  left: number;
}

export function computeOptionsMenuPosition(
  viewportWidth: number,
  viewportHeight: number,
  anchorRect?: MenuRect | null,
  moduleRect?: MenuRect | null,
): MenuPosition {
  const panelWidth = Math.min(190, Math.max(0, viewportWidth - 24));

  let left = 12;
  let top = 56;

  if (anchorRect) {
    left = Math.min(
      Math.max(12, anchorRect.right - panelWidth),
      Math.max(12, viewportWidth - panelWidth - 12),
    );
    top = Math.min(
      Math.max(12, anchorRect.bottom + 4),
      Math.max(12, viewportHeight - 120),
    );
  } else if (moduleRect) {
    left = Math.min(
      Math.max(12, moduleRect.right - panelWidth - 12),
      Math.max(12, viewportWidth - panelWidth - 12),
    );
    top = Math.min(
      Math.max(12, moduleRect.top + 56),
      Math.max(12, viewportHeight - 120),
    );
  }

  return { top, left };
}
